// @spec DHB-STR-029
'use client';

import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { CredentialsList } from '@/features/datahub/integrations/components/credentials-list';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/settings/credentials')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.credentials'),
  }),
  component: CredentialsPage,
});

/**
 * Credentials management page.
 *
 * Shows list of stored credentials for integrations and allows admins
 * to create, edit, rotate, and delete credentials.
 */
function CredentialsPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { isAdmin, isLoading: isRoleLoading } = useOrgRole();

  // Redirect non-admins to dashboard
  useEffect(() => {
    if (!isRoleLoading && !isAdmin) {
      void navigate({ to: '/dashboard' });
    }
  }, [isRoleLoading, isAdmin, navigate]);

  // Don't render content while checking permissions
  if (!isRoleLoading && !isAdmin) {
    return null;
  }

  // Get internal org ID from Clerk organization metadata
  const orgId = (organization?.publicMetadata as { internalId?: string } | undefined)?.internalId;

  // Show skeleton while loading org info
  if (isRoleLoading || !orgId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <header>
            <Skeleton className="h-9 w-64" />
            <Skeleton className="h-5 w-96 mt-2" />
          </header>
          <div className="flex gap-4">
            <Skeleton className="h-10 flex-1" />
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-10 w-32" />
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">{t('credentialsPage.title')}</h1>
          <p className="mt-1 text-muted-foreground">{t('credentialsPage.description')}</p>
        </header>

        <CredentialsList orgId={orgId} />
      </div>
    </div>
  );
}
